/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { fd, td } from '../theme';
import Ic from '../Icon';

export default function Messaging({ msgs = [], addMessage, isLL, activeTenant, tenants = [] }) {
  const [selTenant, setSelTenant] = useState(activeTenant?.id || null);
  const [compose,   setCompose]   = useState('');
  const [sending,   setSending]   = useState(false);
  // Track locally which message IDs the user has read this session
  const [readIds,   setReadIds]   = useState(() => new Set(
    msgs.filter(m => m.read).map(m => m.id)
  ));

  const effectiveTenantId = isLL ? selTenant : activeTenant?.id;

  const thread = effectiveTenantId
    ? msgs.filter(m => (m.tenant_id || m.tid) === effectiveTenantId)
          .slice().sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at))
    : [];

  const tenantName = tid => {
    const t = tenants.find(t => t.id === tid);
    return t ? (t.full_name || t.name) : 'Unknown';
  };

  const conversations = tenants.map(t => {
    const tmsgs = msgs.filter(m => (m.tenant_id || m.tid) === t.id);
    const sorted = tmsgs.slice().sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
    const last   = sorted[sorted.length - 1];
    const unread = tmsgs.filter(m => (m.sender_role || m.from) === 'tenant' && !readIds.has(m.id) && !m.read).length;
    return { tenant: t, last, unread, count: tmsgs.length };
  }).filter(c => c.count > 0 || isLL);

  const unreadTotal = msgs.filter(m =>
    (m.sender_role || m.from) === 'tenant' && !readIds.has(m.id) && !m.read
  ).length;

  const markRead = tid => {
    const ids = msgs
      .filter(m => (m.tenant_id || m.tid) === tid && (m.sender_role || m.from) === 'tenant')
      .map(m => m.id);
    setReadIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
  };

  const sendMessage = async () => {
    if (!compose.trim() || !effectiveTenantId) return;
    const payload = {
      tenant_id:   effectiveTenantId,
      sender_role: isLL ? 'landlord' : 'tenant',
      body:        compose.trim(),
      sent_at:     new Date().toISOString(),
      read:        false,
    };
    setSending(true);
    setCompose('');
    if (addMessage) {
      await addMessage(payload);
    }
    setSending(false);
  };

  const bubble = (m) => {
    const isMine = isLL
      ? (m.sender_role || m.from) === 'landlord'
      : (m.sender_role || m.from) === 'tenant';
    return (
      <div key={m.id} style={{ display:'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
        <div style={{
          maxWidth:'70%',
          background: isMine ? 'linear-gradient(135deg,#16a34a,#4ade80)' : '#21262d',
          color: isMine ? '#0a1a0a' : '#f5f0e8',
          borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          padding:'10px 14px', fontSize:13,
        }}>
          <div>{m.body}</div>
          <div style={{ fontSize:10, marginTop:4, opacity:0.6, textAlign: isMine ? 'right' : 'left' }}>{fd(m.sent_at)}</div>
        </div>
      </div>
    );
  };

  const sendBar = (placeholder) => (
    <div style={{ padding:'12px 18px', borderTop:'1px solid #21262d', display:'flex', gap:8 }}>
      <input className="srch" style={{ margin:0, flex:1 }} value={compose}
        onChange={e => setCompose(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
        placeholder={placeholder} disabled={sending}/>
      <button className="btn btn-g" onClick={sendMessage}
        disabled={!compose.trim() || sending} style={{ padding:'8px 12px' }}>
        <Ic n="send" s={14}/>
      </button>
    </div>
  );

  // ── Tenant view ───────────────────────────────────────────────────────────
  if (!isLL) {
    return (
      <div className="card" style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 130px)', padding:0, overflow:'hidden' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #21262d', display:'flex', alignItems:'center', gap:10 }}>
          <Ic n="msg" s={16} c="#4ade80"/>
          <span style={{ fontWeight:700, color:'#fff', fontSize:15 }}>Messages</span>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'14px 18px', display:'flex', flexDirection:'column', gap:12 }}>
          {thread.length === 0
            ? <div style={{ textAlign:'center', padding:'40px', color:'#484f58' }}>
                <Ic n="msg" s={32} c="#484f58"/>
                <div style={{ marginTop:10, fontSize:13 }}>No messages yet. Start the conversation below.</div>
              </div>
            : thread.map(m => bubble(m))
          }
        </div>
        {sendBar('Type a message...')}
      </div>
    );
  }

  // ── Landlord view ─────────────────────────────────────────────────────────
  return (
    <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:14, height:'calc(100vh - 130px)' }}>
      <div className="card" style={{ padding:0, overflow:'hidden', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #21262d', display:'flex', alignItems:'center', gap:8 }}>
          <Ic n="msg" s={14} c="#4ade80"/>
          <span style={{ fontWeight:700, color:'#fff', fontSize:13 }}>Conversations</span>
          {unreadTotal > 0 && <span className="nb-dot" style={{ marginLeft:'auto' }}>{unreadTotal}</span>}
        </div>
        <div style={{ flex:1, overflowY:'auto' }}>
          {conversations.map(({ tenant: t, last, unread }) => (
            <button key={t.id}
              onClick={() => { setSelTenant(t.id); markRead(t.id); }}
              style={{
                width:'100%', display:'flex', alignItems:'center', gap:10,
                padding:'11px 14px', border:'none',
                background: selTenant === t.id ? 'rgba(34,197,94,.08)' : 'transparent',
                borderLeft: selTenant === t.id ? '2px solid #4ade80' : '2px solid transparent',
                cursor:'pointer', textAlign:'left', transition:'background 0.15s',
              }}>
              <div className="av" style={{ width:34, height:34, fontSize:11, flexShrink:0 }}>
                {(t.full_name||t.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:unread>0?700:500, color:unread>0?'#fff':'#8b949e', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {t.full_name||t.name}
                </div>
                {last && (
                  <div style={{ fontSize:11, color:'#484f58', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:1 }}>
                    {last.body}
                  </div>
                )}
              </div>
              {unread > 0 && <span className="nb-dot">{unread}</span>}
            </button>
          ))}
          {conversations.length === 0 && (
            <div style={{ padding:'24px', textAlign:'center', color:'#484f58', fontSize:12 }}>No tenants yet</div>
          )}
        </div>
      </div>

      <div className="card" style={{ padding:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {!selTenant
          ? <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#484f58', gap:10 }}>
              <Ic n="msg" s={40} c="#484f58"/>
              <div style={{ fontSize:13 }}>Select a conversation</div>
            </div>
          : <>
              <div style={{ padding:'12px 18px', borderBottom:'1px solid #21262d', display:'flex', alignItems:'center', gap:10 }}>
                <div className="av" style={{ width:32, height:32, fontSize:11 }}>
                  {tenantName(selTenant).split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <span style={{ fontWeight:700, color:'#fff', fontSize:14 }}>{tenantName(selTenant)}</span>
              </div>
              <div style={{ flex:1, overflowY:'auto', padding:'14px 18px', display:'flex', flexDirection:'column', gap:12 }}>
                {thread.length === 0
                  ? <div style={{ textAlign:'center', padding:'40px', color:'#484f58' }}>
                      <Ic n="msg" s={28} c="#484f58"/>
                      <div style={{ marginTop:8, fontSize:12 }}>No messages yet. Send a message below.</div>
                    </div>
                  : thread.map(m => bubble(m))
                }
              </div>
              {sendBar(`Message ${tenantName(selTenant)}...`)}
            </>
        }
      </div>
    </div>
  );
}
